import { ReactNode } from 'react';
import { VATConfig } from '~/app/market-config';
import {
	ConfiguredCarCatalogImage,
	ConfiguredCarFeatureFragment,
	ConfiguredCarPackageFeatureFragment,
	ConfiguredCarPackageFragment,
	Incentive,
	PriceFragment,
	Status,
	StatusFragment,
} from './__generated__/graphql';
import { Accessories, Accessory, CarlineInfo, CatalogFeatures, MappedAccessories } from './mapper';
import { PriceOrError, PriceSummary } from './mapper/map-prices';

export enum InteriorSeats {
	STANDARD = 'normal_seats',
	SPORT = 'sports_seats',
}

export type FeatureCategory =
	| 'trimline'
	| 'driveTrain'
	| 'rims'
	| 'steeringWheel'
	| 'seat'
	| 'interiorTile'
	| 'headlight'
	| 'bodyPaint'
	| 'exteriorPaintType'
	| 'accessory'
	| 'package'
	| 'packageFeature'
	| 'interiorPackage'
	| 'incentive';

export type CombinedCatalog = {
	priceSummary: PriceSummary;
	carInfo: CarlineInfo['carInfo'];
	trimlines: CarlineInfo['trimlines'];
	accessoriesInclusive: Accessories;
	accessoriesExclusive: Accessories;
	// accessoriesCombined: Accessories;
	equipmentHighlights: NonNullable<MappedAccessories['equipmentHighlights']>;
} & CatalogFeatures;

type CatalogFeatureType = {
	[K in keyof CatalogFeatures]: NonNullable<CatalogFeatures[K]>[0];
};

export type CatalogItemType = {
	trimline: NonNullable<CarlineInfo['trimlines']>[0];
	driveTrain: NonNullable<NonNullable<CarlineInfo['trimlines']>[0]['driveTrains']>[0];
	accessory: Accessory;
	incentive: Incentive;
} & CatalogFeatureType;

export type ConfiguratorItem = CatalogItemType[keyof CatalogItemType];

export type LoadingFlags = {
	checkingConflicts: boolean;
	catalog: boolean;
	priceSummary: boolean;
	carline: boolean;
	accessories: boolean;
};

export type SelectableOption<T extends FeatureCategory> = {
	type: T;
	featureId: string;
	name: string;
	status: Status | null | undefined;
};

export type FeatureOrPackage = ConfiguredCarFeatureFragment | ConfiguredCarPackageFragment;
export type ContentOrPackage =
	| ConfiguredCarPackageFeatureFragment
	| ConfiguredCarFeatureFragment
	| ConfiguredCarPackageFragment;

export type EnrichedItem = ConfiguredCarPackageFragment & {
	featureId: string;
	__item: ConfiguredCarPackageFragment;
	teaserImageUrl: string;
	type: 'interiorPackage';
	// the next two fields are just required to imitate a catalogItem for the moreInformation
	monthlyRate: PriceOrError | undefined | null;
	totalPrice: PriceFragment | undefined | null;
};

export type TileData = {
	readonly: boolean;
	familyName?: string;
	subFamilyName?: string;
	audiExclusive?: boolean;
	status?: StatusFragment;
	content?: ContentOrPackage[] | null;
	description?: string | null;
	loading: boolean;
	selected: boolean;
	key: string;
	name: string | null | undefined;
	teaserText?: string;
	imgUrl?: string;
	galleryImageUrls?: Array<string | undefined | null>;
	galleryMedia?: Array<ConfiguredCarCatalogImage | null | undefined>;
	standard: boolean;
	price?: {
		totalPrice?: PriceOrError;
		monthlyRate?: number;
		feature: VATConfig;
		priceDisclaimer?: { id: string; text: string };
	};
	disclaimerHeadline?: string;
	disclaimerContent?: string;
	disclaimerStartDate?: string;
	disclaimerEndDate?: string;
	onSelect: () => void;
	textLink?: {
		label: string;
		onTextLinkClick: () => void;
	};
};

export type SelectionCardItem = TileData & {
	incentive?: ReactNode;
	textLink?: {
		label: string;
		onTextLinkClick: () => void;
	};
	onDescriptionToggle?: (value: boolean) => void;
};

export type FilteredItem = TileData & {
	familyName?: string;
	showRenderDisclaimer?: boolean;
};
